import React, { useState, useCallback, useEffect, useRef } from 'react';
import _ from 'lodash';
import {
  GoogleReCaptchaProvider,
  GoogleReCaptcha,
} from 'react-google-recaptcha-v3';
import classnames from 'classnames/bind';
import style from './index.module.scss';
import { Replacement } from '../../../../interface';
import localesReplacement from '../../../../utils/localesReplacement';

interface IQuickSearchInputProps {
  country: string | undefined;
  language: string | undefined;
  value: string;
  onChangeHandler: (value: string, token?: string) => void;
  onClearHandler: (value: React.MouseEvent) => void;
  licence: boolean;
  captcha: boolean;
  replacements: Replacement;
  onMouseDownHandler?: (value: string) => void;
}

const cx = classnames.bind(style);

function QuickSearchInput(props: IQuickSearchInputProps) {
  const {
    country,
    value,
    onChangeHandler,
    onClearHandler,
    licence,
    captcha,
    replacements,
    language,
    onMouseDownHandler,
  } = props;

  const [reCaptchaToken, setRecaptchaToken] = useState<string>('');
  const [refreshReCaptcha, setRefreshReCaptcha] = useState<boolean>(false);
  const refreshIntervalRef = useRef<number | null>(null);
  const siteKey = process.env.REACT_APP_RECAPCHA_SITEKEY;

  const onVerify = useCallback((token: any): void => {
    setRecaptchaToken(token);
    setRefreshReCaptcha(false);
  }, []);

  // Auto refresh token every 60s when captcha enabled and siteKey present
  useEffect(() => {
    if (!captcha || !siteKey) return;
    if (refreshIntervalRef.current) window.clearInterval(refreshIntervalRef.current);
    refreshIntervalRef.current = window.setInterval(() => {
      setRefreshReCaptcha(true);
    }, 60_000);
    return () => {
      if (refreshIntervalRef.current) window.clearInterval(refreshIntervalRef.current);
    };
  }, [captcha, siteKey]);

  return (
    <div className={cx('search-input', { withValue: !!value })}>
      <div className={cx('block')}>
        {value && <label>{localesReplacement(replacements, 'Search')}</label>}
        {!licence || _.isEqual(country, 'USA') || _.isEqual(country, 'AUS') ? (
          <input
            type='text'
            value={value}
            onChange={(ev) => onChangeHandler(ev.target.value)}
            placeholder={localesReplacement(
              replacements,
              _.includes(['AUS', 'NZL'], country)
                ? 'Quick search (e.g. Toyota Hilux 2020)'
                : 'Search by brand or model',
            )}
            onFocus={() => {
              onMouseDownHandler && onMouseDownHandler(value);
            }}
            maxLength={36}
          />
        ) : (
          <>
            {captcha && siteKey && (
              <GoogleReCaptchaProvider
                reCaptchaKey={siteKey}
                language={language?.slice(0, -1).toLowerCase()}
                useRecaptchaNet={false}
                useEnterprise={false}
              >
                <GoogleReCaptcha
                  onVerify={onVerify}
                  refreshReCaptcha={refreshReCaptcha}
                  data-testid='re-captcha'
                />
              </GoogleReCaptchaProvider>
            )}
            {captcha && !siteKey && (
              <div className={cx('captcha-missing-warning')}>
              </div>
            )}
            <input
              type='text'
              value={value}
              onChange={(ev) =>
                captcha
                  ? onChangeHandler(ev.target.value, reCaptchaToken)
                  : onChangeHandler(ev.target.value)
              }
              placeholder={localesReplacement(
                replacements,
                'Search by license, brand or model',
              )}
              onFocus={() => {
                onMouseDownHandler && onMouseDownHandler(value);
              }}
              maxLength={36}
            />
          </>
        )}
      </div>

      {value.length ? (
        <i className={cx('remove')} onClick={onClearHandler}></i>
      ) : (
        <i className={cx('voice')}></i>
      )}
    </div>
  );
}

export default QuickSearchInput;
